import React, { useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
    useGetQuestionsQuery,
    useAddQuestionMutation,
    useUpdateQuestionMutation,
    useDeleteQuestionMutation,
    useGetTechnologiesQuery, // Assuming questions need technologies
    useGetNiveauxQuery, // Assuming questions need niveaux
    useGetCategoriesQuery // Assuming questions need categories
} from '../../redux/api/coachApi';

const QuestionsView = () => {
    const [newQuestion, setNewQuestion] = useState({
        html: '',
        is_published: false,
        maximum_marks: 4,
        technology: '',
        niveau: '',
        categorie: ''
    });
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [error, setError] = useState('');

    // RTK Query hooks
    const { data: questions, isLoading, error: fetchError } = useGetQuestionsQuery();
    const [addQuestion, { isLoading: isAdding }] = useAddQuestionMutation();
    const [updateQuestion, { isLoading: isUpdating }] = useUpdateQuestionMutation();
    const [deleteQuestion, { isLoading: isDeleting }] = useDeleteQuestionMutation();

    // Fetch dependencies for dropdowns
    const { data: technologies, isLoading: isLoadingTechnologies } = useGetTechnologiesQuery();
    const { data: niveaux, isLoading: isLoadingNiveaux } = useGetNiveauxQuery();
    const { data: categories, isLoading: isLoadingCategories } = useGetCategoriesQuery();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newQuestion.html.trim() || !newQuestion.technology || !newQuestion.niveau || !newQuestion.categorie) {
            setError('HTML, Technologie, Niveau et Catégorie sont requis.');
            return;
        }
        setError('');

        try {
            if (editingQuestion) {
                await updateQuestion({ id: editingQuestion.id, ...newQuestion }).unwrap();
            } else {
                await addQuestion(newQuestion).unwrap();
            }
            setNewQuestion({
                html: '',
                is_published: false,
                maximum_marks: 4,
                technology: '',
                niveau: '',
                categorie: ''
            });
            setEditingQuestion(null);
        } catch (err) {
            setError(editingQuestion ?
                "Erreur lors de la mise à jour de la question." :
                "Erreur lors de l'ajout de la question."
            );
            console.error("Error submitting question:", err);
        }
    };

    const handleEdit = (question) => {
        setEditingQuestion(question);
        setNewQuestion({
            html: question.html || '',
            is_published: !!question.is_published,
            maximum_marks: question.maximum_marks || 4,
            technology: question.technology || '',
            niveau: question.niveau || '',
            categorie: question.categorie || ''
        });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) {
            try {
                await deleteQuestion(id).unwrap();
            } catch (err) {
                setError("Erreur lors de la suppression de la question.");
                console.error("Error deleting question:", err);
            }
        }
    };

    if (isLoading || isLoadingTechnologies || isLoadingNiveaux || isLoadingCategories) {
        return <div className="loading">Chargement des questions...</div>;
    }

    if (fetchError) {
        return <div className="error">Erreur lors du chargement des questions: {fetchError.message}</div>;
    }

    return (
        <div className="questions-view-container">
            {/* Form Card */}
            <div className="form-card">
                <h2>{editingQuestion ? 'Modifier la Question' : 'Ajouter une Question'}</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="html">Contenu HTML</label>
                        <textarea
                            id="html"
                            className="form-control"
                            value={newQuestion.html}
                            onChange={(e) => setNewQuestion({ ...newQuestion, html: e.target.value })}
                            placeholder="Entrez le contenu HTML de la question"
                        ></textarea>
                    </div>
                    <div className="form-group">
                        <label htmlFor="maximum_marks">Notes Maximales</label>
                        <input
                            type="number"
                            id="maximum_marks"
                            className="form-control"
                            value={newQuestion.maximum_marks}
                            onChange={(e) => setNewQuestion({ ...newQuestion, maximum_marks: parseInt(e.target.value) })}
                            min="1"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="technology">Technologie</label>
                        <select
                            id="technology"
                            className="form-control"
                            value={newQuestion.technology}
                            onChange={(e) => setNewQuestion({ ...newQuestion, technology: e.target.value })}
                        >
                            <option value="">Sélectionner une technologie</option>
                            {technologies?.map(tech => (
                                <option key={tech.id} value={tech.id}>{tech.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="niveau">Niveau</label>
                        <select
                            id="niveau"
                            className="form-control"
                            value={newQuestion.niveau}
                            onChange={(e) => setNewQuestion({ ...newQuestion, niveau: e.target.value })}
                        >
                            <option value="">Sélectionner un niveau</option>
                            {niveaux?.map(niv => (
                                <option key={niv.id} value={niv.id}>{niv.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="categorie">Catégorie</label>
                        <select
                            id="categorie"
                            className="form-control"
                            value={newQuestion.categorie}
                            onChange={(e) => setNewQuestion({ ...newQuestion, categorie: e.target.value })}
                        >
                            <option value="">Sélectionner une catégorie</option>
                            {categories?.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.nom}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="is_published">Publiée ?</label>
                        <input
                            type="checkbox"
                            id="is_published"
                            checked={newQuestion.is_published}
                            onChange={(e) => setNewQuestion({ ...newQuestion, is_published: e.target.checked })}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn-add-middle"
                        disabled={isAdding || isUpdating}
                    >
                        {editingQuestion ? 'Mettre à jour' : 'Ajouter'}
                    </button>
                </form>
            </div>

            {/* List Card */}
            <div className="list-card">
                <h2>Liste des Questions</h2>
                {questions && questions.length > 0 ? (
                    <div className="list-items-container">
                        {questions.map(question => (
                            <div key={question.id} className="list-item">
                                <div className="content">
                                    <h3>{question.html.substring(0, 100)}...</h3> {/* Show snippet of HTML */}
                                    <p>Technologie: {technologies?.find(tech => tech.id === question.technology)?.name || 'N/A'}</p>
                                    <p>Niveau: {niveaux?.find(niv => niv.id === question.niveau)?.name || 'N/A'}</p>
                                    <p>Catégorie: {categories?.find(cat => cat.id === question.categorie)?.nom || 'N/A'}</p>
                                    <p>Publiée: {question.is_published ? 'Oui' : 'Non'}</p>
                                </div>
                                <div className="actions">
                                    <button
                                        className="btn-icon"
                                        onClick={() => handleEdit(question)}
                                        disabled={isUpdating}
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        className="btn-icon"
                                        onClick={() => handleDelete(question.id)}
                                        disabled={isDeleting}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-data">Aucune question trouvée.</p>
                )}
            </div>
        </div>
    );
};

export default QuestionsView; 